<script setup>
import { EventManager, Events } from "@events";
import { ref, onMounted, computed } from "vue";

const emit = defineEmits(["onVideoRef"]);

// throttle function
function throttle(func, limit) {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// get video props
const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  peer: {
    type: Object,
    required: true,
  },
  autoplay: {
    type: Boolean,
    required: false,
    default: false,
  },
  playsinline: {
    type: Boolean,
    required: false,
    default: false,
  },
  muted: {
    type: Boolean,
    required: false,
    default: false,
  },
  class: {
    type: String,
    required: false,
    default: "",
  },
  enableZoom: {
    type: Boolean,
    required: false,
    default: true,
  },
  disableVideoMutedIcon: {
    type: Boolean,
    default: false,
  },
});

const videoX = ref(0);
const videoY = ref(0);

const factor = ref(1);

const cX = ref(0);
const cY = ref(0);

const videoRef = ref(null);
const parent = ref(null);

const eventEmitter = EventManager.getEventEmitter(props.rtcId);
const isVideoMuted = ref(false);

var style = computed(() => {
  return {
    transform: `translate(${videoX.value}px, ${videoY.value}px) scale(${factor.value})`,
  };
});

var showResetButton = computed(() => {
  return videoX.value != 0 || videoY.value != 0 || factor.value != 1;
});

onMounted(() => {
  emit("onVideoRef", videoRef.value);

  if (props.peer.type == "remote") {
    eventEmitter.on(Events.TextChannelMessageReceived, async ({ data }) => {
      if (data.startsWith("STATUS|VIDEO|")) {
        const parts = data.split("|");

        if (parts[3] == props.rtcId) {
          if (parts[2] == "MUTED") {
            isVideoMuted.value = true;
          } else {
            isVideoMuted.value = false;
          }
        }
      }
    });
  } else if (props.peer.type == "local") {
    eventEmitter.on(Events.VideoMuted, () => {
      isVideoMuted.value = true;
    });

    eventEmitter.on(Events.VideoUnmuted, () => {
      isVideoMuted.value = false;
    });
  }

  if (!props.enableZoom) return;

  let target = videoRef.value.parentElement;

  target.draggable = true;

  target.addEventListener("mousewheel", scroll, false);
  target.addEventListener("dragstart", dragstart);
  target.addEventListener("dragend", dragend);
  target.addEventListener("drag", throttle(drag, 10));
});

function dragstart(e) {
  var img = new Image();
  e.dataTransfer.setDragImage(img, 0, 0);
  if (factor.value == 1) return;

  cX.value = e.clientX;
  cY.value = e.clientY;
}

function drag(e) {
  e.preventDefault();
  if (factor.value == 1) return;

  var x = e.clientX;
  var y = e.clientY;
  var diffX = x - cX.value;
  var diffY = y - cY.value;
  cX.value = x;
  cY.value = y;
  videoX.value += diffX;
  videoY.value += diffY;
}

function dragend(e) {
  if (factor.value == 1) return;

  var diffX = e.clientX - cX.value;
  var diffY = e.clientY - cY.value;
  videoX.value += diffX;
  videoY.value += diffY;
}

function scroll(e) {
  var delta = e.wheelDelta ? e.wheelDelta / 40 : e.detail ? -e.detail : 0;
  if (delta) {
    //factor += (delta < 0 ? -1 : 1) * delta;

    let multiplier = 0.05;

    if (factor.value < 1) {
      multiplier = 0.02;
    } else if (factor.value > 3) {
      multiplier = 0.1;
    }

    factor.value += delta * multiplier;

    if (factor.value < 0.1) factor.value = 0.1;

    if (factor.value > 10) factor.value = 10;
    //$("#resetZoom").css("opacity", factor.value !== 1 ? "0.6" : "0");
  }
}

function resetZoom() {
  videoX.value = 0;
  videoY.value = 0;
  factor.value = 1;
}
</script>

<template>
  <v-btn color="info" v-if="showResetButton" class="reset-zoom" @click="resetZoom"> Reset Zoom </v-btn>
  <video v-bind="props" :style="style" ref="videoRef"></video>

  <v-icon
    v-if="!props.disableVideoMutedIcon && isVideoMuted"
    :class="{ 'video-muted-icon-local': props.peer.type == 'local' }"
    class="video-muted-icon"
    :icon="'mdi-video-off'"
    aria-hidden="true" />
</template>

<style>
.reset-zoom {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.video-muted-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12em;
  color: rgba(255, 255, 255, 0.5);
  z-index: 100;
}

@media screen and (min-width: 800px) {
  .video-muted-icon {
    font-size: 12em;
  }

  .video-muted-icon-local {
    font-size: 5em !important;
  }
}

@media screen and (max-width: 799px) {
  .video-muted-icon {
    font-size: 5em;
  }
}
</style>
